import {
	booleanAttribute,
	Component,
	inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
	ItemComponent,
	ListDetailComponent,
	LinkComponent,
	ITagIcon,
	ITagImage,
	Icons,
} from '@tecalisdev/infinity';
import {
	BOOLEAN_STATUS,
	CHECK,
	IOperationDocument,
	IOperationOcrDetail,
	IOperationPolDetail,
	IProcessDetailResponse,
	PROCESS_STATUS,
} from '../../models/processes.models';
import {
	CONNECTION_TYPE,
	EVALUATION_TYPE,
	OPERATION_STATUS,
	OPERATION_TYPE,
} from '../../../operations/models/operations.models';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MasterService } from '../../../shared/services/master.service';
import { ProcessHelperService } from '../../services/process-helper.service';
import { ModalConfirmationService } from '../../../shared/services/modal-confirmation.service';
import { CommonModule } from '@angular/common';
import { ExpirationDatePipe } from '../../../core/pipes/expiration-date.pipe';
import { RolePermissionsService } from '../../../core/services/role-permissions.service';
import { ItemCheckProhibitedCamerasComponent } from '../../../shared/components/operations/common/item-check-prohibited-cameras/item-check-prohibited-cameras.component';
import { ItemCheckFacialRecognitionComponent } from '../../../shared/components/operations/pol/item-check-facial-recognition/item-check-facial-recognition.component';
import { ItemCheckFraudFaceComponent } from '../../../shared/components/operations/pol/item-check-fraud-face/item-check-fraud-face.component';
import { ItemCheckFraudFaceBlockComponent } from '../../../shared/components/operations/pol/item-check-fraud-face-block/item-check-fraud-face-block.component';
import {
	buildDocumentsCompareTableRows,
	documentTypeLabelForCompare,
	IDocumentsCompareTableRow,
} from '../../../shared/utils/documents-external-compare.util';
import {
	buildFormMatchCompareTableRows,
	IFormMatchCompareTableRow,
	resolveFormMatchDataCheckStatus,
	resolveFormMatchDataLabelKey,
	resolveFormMatchDataModalAlertKey,
	resolveFormMatchDataModalTitleKey,
} from '../../../operations/utils/form-operation-match-compare.util';
import { IOperationFormDetail } from '../../../operations/models/operations.models';
import { ChecksModalsComponent } from './checks-modals/checks-modals.component';
import { ChecksDocsEvalComponent } from './checks-docs-eval/checks-docs-eval.component';

@Component({
	selector: 'app-checks',
	imports: [
		CommonModule,
		ExpirationDatePipe,
		ItemComponent,
		ListDetailComponent,
		LinkComponent,
		TranslatePipe,
		ItemCheckProhibitedCamerasComponent,
		ItemCheckFacialRecognitionComponent,
		ItemCheckFraudFaceComponent,
		ItemCheckFraudFaceBlockComponent,
		ChecksModalsComponent,
		ChecksDocsEvalComponent,
	],
	templateUrl: './checks.component.html',
	styleUrl: './checks.component.scss',
})
export class ChecksComponent implements OnInit, OnChanges, OnDestroy {
	@Input() detail: IProcessDetailResponse;
	@Input() checks: (CHECK | undefined)[] | CHECK[] | null = null;
	/** Clave i18n OCR: sustituye solo comprobaciones OCR por un único mensaje cuando aplica. */
	@Input() exclusiveKoCompletionMessageOcr: string | null = null;
	/** Clave i18n POL: sustituye solo comprobaciones POL cuando esa operación trae el mensaje de cierre. */
	@Input() exclusiveKoCompletionMessagePol: string | null = null;
	protected exclusiveKoCompletionDisplayOcr = '';
	protected exclusiveKoCompletionDisplayPol = '';
	/** Oculta checks OCR «técnicos» (antifraud/actions) y muestra solo el mensaje de cierre. */
	protected suppressOcrKoChecks = false;
	/** Oculta checks POL habituales y muestra solo el mensaje de cierre en el bloque POL. */
	protected suppressPolKoChecks = false;
	private exclusiveLangSub?: Subscription;
	@Input({ transform: booleanAttribute }) showOnlyKO: boolean = false;
	@Input({ transform: booleanAttribute }) showOperationPrefix: boolean = true;

	public masterService = inject(MasterService);
	public processHelper = inject(ProcessHelperService);
	public rolePermissions = inject(RolePermissionsService);
	private modalConfirmationService = inject(ModalConfirmationService);
	private trans = inject(TranslateService);

	public showAeatModal: boolean = false;
	public showMrzModal: boolean = false;
	public showFormMatchModal: boolean = false;
	public compareModalDoc: IOperationDocument | null = null;
	public compareModalRows: IDocumentsCompareTableRow[] = [];
	public OPERATION_STATUS = OPERATION_STATUS;
	public PROCESS_STATUS = PROCESS_STATUS;
	public BOOLEAN_STATUS = BOOLEAN_STATUS;
	public CONNECTION_TYPE = CONNECTION_TYPE;
	public EVALUATION_TYPE = EVALUATION_TYPE;
	public OPERATION_TYPE = OPERATION_TYPE;
	public CHECK = CHECK;
	private readonly ANTIFRAUD_ERRORS = [2034, 2035, 2036, 2037, 2101];

	ngOnInit(): void {
		// Si no se proporciona checks, usar todos los disponibles (excepto checks solo para wrappers de operación).
		if (this.checks === null || this.checks.length === 0) {
			this.checks = Object.values(CHECK).filter(
				(value) => typeof value === 'number' && value !== CHECK.DOCUMENTS_EXTERNAL_COMPARE,
			) as CHECK[];
		}
		this.exclusiveLangSub = this.trans.onLangChange.subscribe(() => this.refreshExclusiveKoDisplay());
		this.refreshExclusiveKoDisplay();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (
			changes['exclusiveKoCompletionMessageOcr'] ||
			changes['exclusiveKoCompletionMessagePol'] ||
			changes['showOperationPrefix']
		) {
			this.refreshExclusiveKoDisplay();
		}
	}

	ngOnDestroy(): void {
		this.exclusiveLangSub?.unsubscribe();
	}

	private refreshExclusiveKoDisplay(): void {
		const ko = this.exclusiveKoCompletionMessageOcr?.trim();
		const kp = this.exclusiveKoCompletionMessagePol?.trim();
		this.suppressOcrKoChecks = !!ko;
		this.suppressPolKoChecks = !!kp;
		this.exclusiveKoCompletionDisplayOcr = ko ? this.buildExclusiveKoTitle(ko, OPERATION_TYPE.OCR) : '';
		this.exclusiveKoCompletionDisplayPol = kp ? this.buildExclusiveKoTitle(kp, OPERATION_TYPE.POL) : '';
	}

	/** Misma pauta que `detailKoGeneral` / CHECK_KO_GENERAL: «Operación - » + mensaje. */
	private buildExclusiveKoTitle(i18nKey: string, operation: OPERATION_TYPE.OCR | OPERATION_TYPE.POL): string {
		const body = this.trans.instant(i18nKey);
		if (!this.showOperationPrefix) {
			return body;
		}
		const prefixKey =
			operation === OPERATION_TYPE.OCR
				? 'PROCESSES.DETAIL.KO_ERROR_TITLE_PREFIX_OCR'
				: 'PROCESSES.DETAIL.KO_ERROR_TITLE_PREFIX_POL';
		return `${this.trans.instant(prefixKey)}${body}`;
	}

	public showModalGenericInfo(type: string, info: string): void {
		this.modalConfirmationService.show({
			title: this.trans.instant(`PROCESSES.DETAIL.${type}_MODAL.TITLE`),
			description: info,
		});
	}

	public getTagIconItem(status: BOOLEAN_STATUS | string): ITagIcon {
		return status === 'OK'
			? { icon: 'icon-check', status: 'success', size: 'sm' }
			: { icon: 'icon-alert', status: 'error', size: 'sm' };
	}

	public getTagImageByError(code: number): ITagImage {
		let icon: Icons = 'icon-info';
		if (code && this.ANTIFRAUD_ERRORS.includes(code)) icon = 'icon-shield';

		return { icon, status: 'simple' };
	}

	/**
	 * Obtiene las claves (UUIDs) de un objeto de documentos.
	 *
	 * @param docRecord - El objeto que contiene documentos con UUIDs como claves.
	 * @returns Un array con las claves del objeto.
	 */
	public getDocumentKeys(docRecord: Record<string, any>): string[] {
		return Object.keys(docRecord || {});
	}

	public buildDocumentsCompareTableRowsForDoc(doc: IOperationDocument | null): IDocumentsCompareTableRow[] {
		return buildDocumentsCompareTableRows(doc, this.detail?.data?.DOCUMENTS ?? null, this.trans);
	}

	public get compareModalHasMismatch(): boolean {
		return this.compareModalRows.some((r) => r.notification);
	}

	public documentCompareTypeLabel(doc: IOperationDocument): string {
		return documentTypeLabelForCompare(doc, this.masterService, this.trans);
	}

	public openDocumentsCompareModal(doc: IOperationDocument, event?: Event): void {
		try {
			event?.preventDefault();
			event?.stopPropagation();
			this.compareModalDoc = doc;
			this.compareModalRows = this.buildDocumentsCompareTableRowsForDoc(doc);
		} catch {
			this.closeDocumentsCompareModal();
		}
	}

	public closeDocumentsCompareModal(): void {
		this.compareModalDoc = null;
		this.compareModalRows = [];
	}

	/**
	 * Monta `app-checks-docs-eval` solo si va a pintar al menos un item.
	 * Evita hosts vacíos que el CSS de `.checks-divider` trataba como separadores (NIF-843).
	 */
	protected shouldRenderDocsEval(check: CHECK | undefined | null): boolean {
		if (check == null || !this.detail) {
			return false;
		}
		switch (check) {
			case CHECK.DOCUMENT_STATE: {
				const docs = this.detail.data?.DOCUMENTS;
				if (!docs?.document?.length) {
					return false;
				}
				return this.showOnlyKO ? docs.state === OPERATION_STATUS.KO : true;
			}
			case CHECK.DOCUMENTS_EXTERNAL_COMPARE: {
				const docs = this.detail.data?.DOCUMENTS?.document ?? [];
				return docs.some((doc) => this.buildDocumentsCompareTableRowsForDoc(doc).length > 0);
			}
			case CHECK.FINANCIAL_STATE: {
				const evaluation = this.detail.data?.EVALUATION;
				if (!evaluation) {
					return false;
				}
				return this.showOnlyKO ? evaluation.state === OPERATION_STATUS.KO : true;
			}
			case CHECK.ATTORNEY_POWERS_STATE: {
				const attorney = this.detail.data?.ATTORNEY_POWERS;
				if (!attorney) {
					return false;
				}
				return this.showOnlyKO ? attorney.state === OPERATION_STATUS.KO : true;
			}
			case CHECK.EVALUATION_MATCH_DATA: {
				const evaluation = this.detail.data?.EVALUATION;
				if (!evaluation) {
					return false;
				}
				return this.showOnlyKO ? evaluation.state === OPERATION_STATUS.KO : true;
			}
			case CHECK.FORM_MATCH_DATA:
				return this.shouldShowFormMatchData();
			default:
				return false;
		}
	}

	protected shouldShowOcrAntifraud(field: keyof IOperationOcrDetail['antifraud']): boolean {
		const ocr = this.detail?.data?.OCR;
		return this.processHelper.shouldDisplayAntifraudCheck(ocr?.state, ocr?.antifraud?.[field], {
			showOnlyKo: this.showOnlyKO,
			// RESULT showOnlyKO: no ocultar KO por estado OK de la op (NIF-819).
			alwaysShowResult: this.showOnlyKO,
		});
	}

	/** NIF-997: «Ver detalles» MRZ solo si hay líneas persistidas (mismo criterio que AEAT.details). */
	protected hasOcrMrzLines(): boolean {
		const ocr = this.detail?.data?.OCR;
		return !!(ocr?.mrz1 || ocr?.mrz2 || ocr?.mrz3);
	}

	/** NIF-817: fraude visual OCR (int/pre o superadmin en pro). */
	protected canShowOcrImageFraud(): boolean {
		return this.processHelper.canShowOcrImageFraudChecks();
	}

	protected ocrImageFraudBySide(
		field: 'SCREENPHOTO' | 'PHOTOCOPY' | 'MANIPULATED_PHOTO',
	): Array<{ document_side?: string | null; state?: string; details?: number | null }> {
		return this.detail?.data?.OCR?.actions?.[field]?.by_side ?? [];
	}

	/** Solo entradas KO (NIF-817: detalle legible). */
	protected ocrImageFraudKoEntries(
		field: 'SCREENPHOTO' | 'PHOTOCOPY' | 'MANIPULATED_PHOTO',
	): Array<{ document_side?: string | null; state?: string; details?: number | null; captureIndex: number }> {
		return this.ocrImageFraudBySide(field)
			.map((entry, index) => ({ ...entry, captureIndex: index }))
			.filter((entry) => entry.state === 'KO' && entry.details != null);
	}

	protected ocrImageFraudReliability(score: number | null | undefined): string {
		if (score == null || Number.isNaN(Number(score))) {
			return '';
		}
		return (-Number(score) * 100 + 100).toFixed(2);
	}

	/**
	 * Anverso/reverso si se conoce; si no, ordinal de captura (1ª Imagen…).
	 * @param captureIndex índice 0-based en by_side
	 */
	protected ocrDocumentSideLabel(side: string | null | undefined, captureIndex = 0): string {
		if (side === 'front') {
			return this.trans.instant('PROCESSES.DETAIL.DOCUMENT_SIDE.FRONT');
		}
		if (side === 'back') {
			return this.trans.instant('PROCESSES.DETAIL.DOCUMENT_SIDE.BACK');
		}
		if (side === 'unique') {
			return this.trans.instant('PROCESSES.DETAIL.DOCUMENT_SIDE.UNIQUE');
		}
		return this.trans.instant('PROCESSES.DETAIL.DOCUMENT_SIDE.IMAGE_N', { n: captureIndex + 1 });
	}

	protected isOcrImageFraudKo(
		field: 'SCREENPHOTO' | 'PHOTOCOPY' | 'MANIPULATED_PHOTO',
	): boolean {
		return this.detail?.data?.OCR?.antifraud?.[field] === BOOLEAN_STATUS.KO;
	}

	protected shouldShowPolAntifraud(field: keyof IOperationPolDetail['antifraud']): boolean {
		const pol = this.detail?.data?.POL;
		return this.processHelper.shouldDisplayAntifraudCheck(pol?.state, pol?.antifraud?.[field], {
			showOnlyKo: this.showOnlyKO,
			alwaysShowResult: this.showOnlyKO || field === 'ANTISPOOF_LAB',
		});
	}

	protected shouldShowFormMatchData(): boolean {
		const form = this.detail?.data?.FORM as IOperationFormDetail | undefined;
		const status = resolveFormMatchDataCheckStatus(form);
		if (status === undefined) {
			return false;
		}
		return this.showOnlyKO ? status === BOOLEAN_STATUS.KO : true;
	}

	public get formMatchCompareRows(): IFormMatchCompareTableRow[] {
		const form = this.detail?.data?.FORM as IOperationFormDetail | undefined;
		if (!form) {
			return [];
		}
		return buildFormMatchCompareTableRows(form, {
			translate: (key) => this.trans.instant(key),
			hasTranslation: (key) => this.masterService.hasTranslation(key),
			locale: this.trans.currentLang,
		}, undefined, this.detail?.data?.OCR ?? null);
	}

	public get formMatchCompareHasMismatch(): boolean {
		return this.formMatchCompareRows.some((row) => row.notification);
	}

	public get formMatchDataLabelKey(): string {
		const form = this.detail?.data?.FORM as IOperationFormDetail | undefined;
		return resolveFormMatchDataLabelKey(form, this.detail?.data?.OCR ?? null);
	}

	public get formMatchDataModalTitleKey(): string {
		const form = this.detail?.data?.FORM as IOperationFormDetail | undefined;
		return resolveFormMatchDataModalTitleKey(form, this.detail?.data?.OCR ?? null);
	}

	public get formMatchDataModalAlertKey(): string {
		const form = this.detail?.data?.FORM as IOperationFormDetail | undefined;
		return resolveFormMatchDataModalAlertKey(form, this.detail?.data?.OCR ?? null);
	}

	public get formMatchCheckStatus(): BOOLEAN_STATUS | undefined {
		return resolveFormMatchDataCheckStatus(this.detail?.data?.FORM as IOperationFormDetail | undefined);
	}

	protected antispoofLabPhasesDisagree(): boolean | null {
		const details = this.detail?.data?.POL?.actions?.ANTISPOOF_LAB?.details as
			| { phases_summary?: { any_disagree?: boolean } }
			| undefined;
		const disagree = details?.phases_summary?.any_disagree;
		if (typeof disagree !== 'boolean') {
			return null;
		}
		return disagree;
	}
}
